import React from 'react';
import { SortingRule } from '@components/table';
import { UserContext } from '..';
import { useWrapRequest } from '../../../components/hooks/use-wrap-request';
import { useSearchReducer } from '../../../components/hooks/useSearchReducer';
import { Api } from '../../../core/api';
import {
    PageUserBadgeV2AdminDto,
    UserBadgeV2AdminDto
} from '../../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../../core/tsdi';

export type { PageUserBadgeV2AdminDto, UserBadgeV2AdminDto };

export function useUserBadgesStore() {
    const { fitnessActivityClient } = injectTSDI(Api);
    const user = React.useContext(UserContext);

    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >({ id: 'earnedAt', desc: false });

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    React.useEffect(() => {
        if (sorting) {
            return dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const userBadges = useWrapRequest(
        async (
            userId: string,
            searchParams?: { page?: number; size?: number; sort?: string }
        ) => {
            // Convert sort format from +earnedAt/-earnedAt to earnedAt,desc/earnedAt,asc
            let sortParam: string[] | undefined;
            if (searchParams?.sort) {
                const sortStr = searchParams.sort;
                if (sortStr.startsWith('+')) {
                    sortParam = [`${sortStr.substring(1)},desc`];
                } else if (sortStr.startsWith('-')) {
                    sortParam = [`${sortStr.substring(1)},asc`];
                } else {
                    sortParam = [sortStr];
                }
            }

            const response =
                await fitnessActivityClient.admin.userBadgeV2AdminControllerGetUserBadgesByUserId(
                    userId,
                    {
                        page: searchParams?.page,
                        size: searchParams?.size,
                        sort: sortParam
                    }
                );

            return {
                content: response.data?.content || [],
                number: response.data?.number ?? 0,
                size: response.data?.size ?? 30,
                totalPages: response.data?.totalPages ?? 0,
                totalElements: response.data?.totalElements ?? 0
            } as PageUserBadgeV2AdminDto;
        },
        {
            deps: [user.id.toString(), searchParams],
            defaultData: {
                content: [],
                number: 0,
                size: 30,
                totalPages: 0,
                totalElements: 0
            }
        }
    );

    const removeBadge = async (badgeId: number, reason?: string) => {
        await fitnessActivityClient.admin.userBadgeV2AdminControllerRevokeUserBadge(
            badgeId,
            {
                revokedBy: 'admin-portal',
                reason
            }
        );
    };

    const deleteBadgePermanently = async (badgeId: number) => {
        await fitnessActivityClient.admin.userBadgeV2AdminControllerHardDeleteUserBadge(
            badgeId
        );
    };

    const refreshBadges = () => {
        userBadges.request([user.id.toString(), searchParams]);
    };

    return {
        userBadges,
        sorting,
        setSorting,
        searchParams,
        dispatch,
        removeBadge,
        deleteBadgePermanently,
        refreshBadges,
        userId: user.id.toString()
    };
}
